import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from '@/api/business'
import { createParameterConferenceHub } from '@/api/business/processhub'
import { ParameterConferenceHubKeys } from '@/queries/useParameterConferenceHubQuery'
import Button from '@/components/Button'  
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'   
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/components/ui/form'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from "@/lib/utils" 
import { Calendar } from "@/components/ui/calendar"
    import { useCompaniesQuery } from '@/queries/useCompaniesQuery' 
import { CalendarIcon } from 'lucide-react'
import { formatDateToString, getDateFromId, getIdDataFromDate } from '@/utils/date'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void
}

const AddDialog = ({ isOpen, onClose }: AddDialogProps) => {
    const queryClient = useQueryClient() 
    const { toast } = useToast() 
 
    const { data: dataCompanies } = useCompaniesQuery()
    
    const schema = z.object({ 
        SK_EMPRESA_FORNECEDOR: z.string(), 
        SK_EMPRESA_CLIENTE: z.string(), 
        SK_TEMPO_VIGENCIA_INICIAL: z.number(),
        SK_TEMPO_VIGENCIA_FINAL: z.number(),
    })

    const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {  
            SK_EMPRESA_FORNECEDOR: '', 
            SK_EMPRESA_CLIENTE: '', 
            SK_TEMPO_VIGENCIA_INICIAL:  0,
            SK_TEMPO_VIGENCIA_FINAL: 0,
        },
    })
    
    const { mutate, isPending: mutateLoading } = useMutation({
        mutationFn: createParameterConferenceHub,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ParameterConferenceHubKeys.lists() })
            onClose()
            form.reset() 
            toast({
                title: 'Parâmetro de Confêrencia Hub criado com sucesso',
            })
        },
        onError: (err) => {
            let errorMessage =
                'Não foi possível criar um parâmetro de confêrencia hub. Tente novamente mais tarde.'

            if (isAxiosError(err)) {
                errorMessage = err.response?.data.message || errorMessage
            }

            toast({
                title: 'Erro ao criar o Parâmetro de Confência Hub',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSubmit = (data: z.infer<typeof schema>) => {  
        mutate( data )
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[400px] overflow-auto'  aria-describedby='' >
                <DialogHeader className='px-2'>
                    <DialogTitle>Adicionar Parâmetro de Confência Hub</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    > 
                        <FormField
                            name="SK_EMPRESA_FORNECEDOR"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Empresa Fornecedor</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()}>
                                        <FormControl>
                                            <SelectTrigger className='flex'>
                                                <SelectValue placeholder="" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className=''>
                                            {dataCompanies?.map((companies) => (
                                                <SelectItem key={companies.SK_EMPRESA} value={companies.SK_EMPRESA}>{companies.DS_ABREVIATURA}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /> 
                        <FormField
                            name="SK_EMPRESA_CLIENTE"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Empresa Cliente</FormLabel>
                                    <Select onValueChange={(value) => field.onChange(value)} defaultValue={field.value.toString()}>
                                        <FormControl>
                                            <SelectTrigger className='flex'>
                                                <SelectValue placeholder="" />
                                            </SelectTrigger>
                                        </FormControl>
                                        <SelectContent className=''>
                                            {dataCompanies?.map((companies) => (
                                                <SelectItem key={companies.SK_EMPRESA} value={companies.SK_EMPRESA}>{companies.DS_ABREVIATURA}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <FormMessage />
                                </FormItem>
                            )}
                        /> 
                        <FormField
                            name="SK_TEMPO_VIGENCIA_INICIAL"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className="flex flex-col">
                                    <FormLabel>Vigência Incial</FormLabel>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                            variant={"outline"}
                                            className={cn(
                                                "pl-3 text-left font-normal",
                                                !field.value && "text-muted-foreground"
                                            )}
                                            >
                                            {field.value ? (
                                                formatDateToString(field.value)
                                            ) : (
                                                <span>Selecionar Vigência Incial</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </Button>
                                        </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={getDateFromId(field.value)} 
                                            onSelect={(e) => { 
                                                    field.onChange(e ? Number(getIdDataFromDate(e)) : 0)
                                            }}
                                            initialFocus
                                        />
                                        </PopoverContent>
                                    </Popover> 
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            name="SK_TEMPO_VIGENCIA_FINAL"
                            control={form.control}
                            render={({ field }) => (
                                <FormItem className="flex flex-col">
                                    <FormLabel>Vigência Final</FormLabel>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                        <FormControl>
                                            <Button
                                            variant={"outline"}
                                            className={cn(
                                                "pl-3 text-left font-normal",
                                                !field.value && "text-muted-foreground"
                                            )}
                                            >
                                            {field.value ? (
                                                formatDateToString(field.value)
                                            ) : (
                                                <span>Selecionar Vigência Final</span>
                                            )}
                                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                            </Button>
                                        </FormControl>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            mode="single"
                                            selected={getDateFromId(field.value)}
                                            onSelect={(e) => { 
                                                    field.onChange(e ? Number(getIdDataFromDate(e)) : 0)
                                            }}
                                            initialFocus
                                        />
                                        </PopoverContent>
                                    </Popover> 
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button
                                type="button"
                                onClick={onClose}
                                variant="ghost"
                            >
                                Cancelar
                            </Button>
                            <Button type="submit" isLoading={mutateLoading}>
                                Confirmar
                            </Button>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default AddDialog
